import { useState } from 'react';
import { Download, ChevronLeft } from 'lucide-react';
import ExportDialog from './components/ExportDialog';
import { useIsMobile } from './hooks/useMediaQuery';

export default function FloatingDownloadButton({
    data,
    fieldOrder,
    sheetName,
    pagination,
    filterObject,
    label = "Download Excel"
}) {
    const [isExpanded, setIsExpanded] = useState(false);
    const [dialogOpen, setDialogOpen] = useState(false);
    //Hidden on mobile: the hover-to-expand affordance has no touch equivalent
    //(the collapsed button sits off-screen). The mobile sheet toolbar exposes
    //the same export via exportSheetToExcel.
    const isMobile = useIsMobile();
    if (isMobile) return null;

    const handleDownload = () => setDialogOpen(true);

    return (
        <>
        <ExportDialog
            open={dialogOpen}
            onOpenChange={setDialogOpen}
            pageRows={data}
            fieldOrder={fieldOrder}
            sheetName={sheetName}
            pagination={pagination}
            filterObject={filterObject}
        />
        <div
            className="floating-download-button fixed right-0 top-1/2 -translate-y-1/2 z-[9999] transition-all duration-300 ease-out"
            style={{
                transform: `translate(${isExpanded ? '0' : '100%'}, -50%)`,
            }}
            onMouseEnter={() => setIsExpanded(true)}
            onMouseLeave={() => setIsExpanded(false)}
        >
            <button
                id="export-open-button"
                onClick={handleDownload}
                className="flex items-center gap-3 px-6 py-4 bg-[var(--theme-success)] text-white rounded-l-2xl shadow-lg hover:shadow-xl transition-all duration-200 group relative overflow-hidden"
                title="Download table as Excel spreadsheet"
            >
                {/* Animated background shimmer */}
                <div 
                    className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent translate-x-full group-hover:-translate-x-full transition-transform duration-1000"
                    style={{ width: '50%' }}
                />
                
                {/* Decorative edge pattern */}
                <div className="absolute left-0 top-0 bottom-0 w-1 bg-[var(--theme-success)] opacity-50" />
                
                {/* Chevron indicator */}
                <ChevronLeft 
                    size={20} 
                    className={`transition-transform duration-300 ${isExpanded ? 'rotate-180' : ''}`}
                />
                
                {/* Text */}
                <span className="font-medium whitespace-nowrap">{label}</span>
                
                {/* Icon container with pulse effect */}
                <div className="relative">
                    <div className="absolute inset-0 bg-white/30 rounded-full animate-ping opacity-75" style={{ animationDuration: '2s' }} />
                    <Download size={24} className="relative z-10" />
                </div>
                
                {/* Subtle glow on hover */}
                <div className="absolute inset-0 bg-white/0 group-hover:bg-white/10 transition-colors duration-200 rounded-l-2xl" />
            </button>
            
            {/* Visible tab/edge that invites hovering */}
            <div className="absolute left-0 top-1/2 -translate-y-1/2 w-2 h-20 -ml-2">
                {/* Gradient edge */}
                <div className="absolute inset-0 rounded-l-lg" style={{ background: 'linear-gradient(to left, transparent, rgba(22, 163, 74, 0.45))' }} />
                
                {/* Subtle dots pattern */}
                <div className="absolute left-0 top-1/2 -translate-y-1/2 flex flex-col gap-2 pl-0.5">
                    <div className="w-1 h-1 bg-white/60 rounded-full animate-pulse" style={{ animationDelay: '0s', animationDuration: '2s' }} />
                    <div className="w-1 h-1 bg-white/60 rounded-full animate-pulse" style={{ animationDelay: '0.3s', animationDuration: '2s' }} />
                    <div className="w-1 h-1 bg-white/60 rounded-full animate-pulse" style={{ animationDelay: '0.6s', animationDuration: '2s' }} />
                </div>
            </div>
        </div>
        </>
    );
}